import { createBrowserRouter, Outlet, redirect } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import App from './App';
import GeolocationProvider from './hooks/GeolocationProvider';
import PredictionProvider from './hooks/PredictionProvider';
import MapProvider from './hooks/MapProvider';
import RouterError from './views/error/RouterError';
import HomeSkeleton from './views/home/Home.skeleton';
import PushNotificationsTest from './views/push-notifications/PushNotificationsTest';
import Home from './views/home/Home';
import SubEventProvider from './hooks/SubEventProvider.tsx';
import ParticipantDetailsProvider from './views/leaderboard/components/details/hooks/ParticipantDetailsProvider';
import { AppLayoutSkeleton } from '@fastnorth/kraken/components/page-layout';
import Layout from './layout/Layout';
import FilmstripSkeleton from './views/components/film-strip/Filmstrip.skeleton';
import SearchLayout from './layout/SearchLayout';
import SessionProvider from './hooks/SessionProvider';
import { ApolloProvider } from '@apollo/client';
import { buildGQLClient } from './graphql/apollo-client';
import RedirectToDefaultRaceUrl from './components/RedirectToDefaultRaceUrl.tsx';
import RedirectWhenNoGallery from './components/RedirectWhenNoGallery.tsx';
import ContextualLoadingSkeleton from './hooks/components/ContextualLoadingSkeleton.tsx';
import AppProvider from './hooks/AppProvider.tsx';
import ThemeProvider from '@fastnorth/kraken/theme/provider';
import { Flex } from '@fastnorth/kraken/components/layout';
import FinisherCertificateContainer from './views/leaderboard/components/details/components/finisher-certificate/FinisherCertificateContainer.tsx';

const EventProgress = lazy(() => import('./views/event-progress/EventProgress'));
const Following = lazy(() => import('./views/following/Following'));
const ResultsLayout = lazy(() => import('./layout/ResultsLayout'));
const Awards = lazy(() => import('./views/awards/Awards'));
const Leaderboard = lazy(() => import('./views/leaderboard/Leaderboard'));
const Details = lazy(() => import('./views/leaderboard/components/details/Details'));
const Photos = lazy(() => import('./views/photos/Photos'));
const Search = lazy(() => import('./views/search/Search'));
const Statistics = lazy(() => import('./views/statistics/Statistics'));

const router = createBrowserRouter(
  [
    {
      path: '/search',
      element: (
        <Suspense fallback={<AppLayoutSkeleton />}>
          <ApolloProvider client={buildGQLClient()}>
            <SessionProvider>
              <SearchLayout>
                <Search />
              </SearchLayout>
            </SessionProvider>
          </ApolloProvider>
        </Suspense>
      ),
      errorElement: <RouterError />,
    },
    {
      path: 'events/:eventUniqueCode/finisher-certificate/:certificateId?',
      element: (
        <Suspense fallback={<ContextualLoadingSkeleton />}>
          <ApolloProvider client={buildGQLClient()}>
            <AppProvider>
              <SubEventProvider sampleMode>
                <ParticipantDetailsProvider sampleMode>
                  <ThemeProvider>
                    <Flex className={'w-full items-center justify-center'}>
                      <FinisherCertificateContainer />
                    </Flex>
                  </ThemeProvider>
                </ParticipantDetailsProvider>
              </SubEventProvider>
            </AppProvider>
          </ApolloProvider>
        </Suspense>
      ),
      errorElement: <RouterError />,
    },
    {
      path: '/',
      element: <App />,
      errorElement: (
        <Suspense fallback={<AppLayoutSkeleton />}>
          <RouterError />
        </Suspense>
      ),
      children: [
        {
          index: true,
          loader: () => redirect('/search'),
        },
        {
          path: 'events/:eventUniqueCode',
          element: (
            <Suspense fallback={<AppLayoutSkeleton />}>
              <Layout>
                <ResultsLayout />
              </Layout>
            </Suspense>
          ),
          errorElement: (
            <Suspense fallback={<AppLayoutSkeleton />}>
              <RouterError />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<HomeSkeleton />}>
                  <Home />
                </Suspense>
              ),
              errorElement: (
                <Suspense fallback={<HomeSkeleton />}>
                  <RouterError />
                </Suspense>
              ),
            },
            {
              path: 'photos',
              element: (
                <RedirectWhenNoGallery>
                  <Suspense fallback={<FilmstripSkeleton />}>
                    <Photos />
                  </Suspense>
                </RedirectWhenNoGallery>
              ),
              errorElement: (
                <Suspense fallback={<FilmstripSkeleton />}>
                  <RouterError />
                </Suspense>
              ),
            },
            {
              path: 'statistics',
              element: <RedirectToDefaultRaceUrl targetPath={'statistics'} />,
              errorElement: <RouterError />,
            },
            {
              path: 'race/:raceId',
              element: (
                <Suspense fallback={<ContextualLoadingSkeleton />}>
                  <SubEventProvider>
                    <Outlet />
                  </SubEventProvider>
                </Suspense>
              ),
              errorElement: (
                <Suspense fallback={<ContextualLoadingSkeleton />}>
                  <RouterError />
                </Suspense>
              ),
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<ContextualLoadingSkeleton />}>
                      <Leaderboard />
                    </Suspense>
                  ),
                  errorElement: <RouterError />,
                },
                {
                  path: 'statistics',
                  element: (
                    <Suspense fallback={<ContextualLoadingSkeleton />}>
                      <Statistics />
                    </Suspense>
                  ),
                  errorElement: <RouterError />,
                },
                {
                  path: 'awards',
                  element: (
                    <Suspense fallback={<ContextualLoadingSkeleton />}>
                      <Awards />
                    </Suspense>
                  ),
                  errorElement: <RouterError />,
                },
                {
                  path: 'participant/:resultUniqueCode',
                  element: (
                    <Suspense fallback={<ContextualLoadingSkeleton />}>
                      <ParticipantDetailsProvider>
                        <Details />
                      </ParticipantDetailsProvider>
                    </Suspense>
                  ),
                  errorElement: <RouterError />,
                },
              ],
            },
            {
              path: 'progress',
              element: (
                <Suspense fallback={<ContextualLoadingSkeleton />}>
                  <EventProgress />
                </Suspense>
              ),
              errorElement: (
                <Suspense fallback={<ContextualLoadingSkeleton />}>
                  <RouterError />
                </Suspense>
              ),
            },
            {
              path: 'following/:resultSubEventId?',
              element: (
                <Suspense fallback={<ContextualLoadingSkeleton />}>
                  <PredictionProvider>
                    <GeolocationProvider>
                      <MapProvider>
                        <Following />
                      </MapProvider>
                    </GeolocationProvider>
                  </PredictionProvider>
                </Suspense>
              ),
              errorElement: (
                <Suspense fallback={<ContextualLoadingSkeleton />}>
                  <RouterError />
                </Suspense>
              ),
            },
            {
              path: 'awards',
              loader: ({ params }) => redirect(`/events/${params.eventUniqueCode}/race/all/awards`),
            },
            {
              path: 'push-notifications',
              element: <PushNotificationsTest />,
              errorElement: <RouterError />,
            },
          ],
        },
      ],
    },
  ],
  {
    basename: import.meta.env.BASE_URL || '/v3',
  }
);

export default router;
